import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Article } from '../../services/smartReadApi';
import {
  Box,
  Card,
  CardBody,
  Heading,
  Text,
  Badge,
  HStack,
  VStack,
  Flex,
  Icon,
  useColorModeValue,
  Link,
} from '@chakra-ui/react';
import { FiBook, FiCalendar, FiGlobe } from 'react-icons/fi';

interface ArticleCardProps {
  article: Article;
}

export function ArticleCard({ article }: ArticleCardProps) {
  // Extract first 120 characters of content for the preview (increased from 100)
  const previewContent = article.content.length > 120
    ? `${article.content.substring(0, 120)}...`
    : article.content;

  // Format the date for display
  const formattedDate = new Date(article.created_at).toLocaleDateString();

  // Get language display name
  const getLanguageDisplayName = (lang: string): string => {
    const languageMap: Record<string, string> = {
      'english': 'English',
      'dutch': 'Dutch',
      'hebrew': 'Hebrew',
    };
    return languageMap[lang.toLowerCase()] || lang;
  };

  // Colors and styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const highlightColor = useColorModeValue('purple.500', 'purple.300');
  const textColor = useColorModeValue('gray.800', 'white');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const badgeBg = useColorModeValue('purple.100', 'purple.900');
  const badgeColor = useColorModeValue('purple.700', 'purple.200');
  const lengthBadgeBg = useColorModeValue('blue.100', 'blue.900');
  const lengthBadgeColor = useColorModeValue('blue.700', 'blue.200');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Link
      as={RouterLink}
      to={`/smart-read/${article.id}`}
      textDecoration="none"
      _hover={{ textDecoration: 'none' }}
      h="100%"
      display="block"
    >
      <Card
        bg={cardBg}
        borderColor={cardBorder}
        borderWidth="1px"
        borderRadius="xl"
        boxShadow="md"
        overflow="hidden"
        transition="all 0.3s"
        h="100%"
        _hover={{
          transform: 'translateY(-4px)',
          boxShadow: 'lg',
          borderColor: highlightColor,
        }}
      >
        <Box bg={highlightColor} h="8px" w="full" />
        <CardBody p={5}>
          <VStack spacing={4} align="stretch">
            <Flex justify="space-between" align="flex-start">
              <HStack spacing={2} align="center">
                <Icon as={FiBook} color={highlightColor} />
                <Heading
                  size="md"
                  color={textColor}
                  noOfLines={2}
                  fontWeight="bold"
                >
                  {article.topic}
                </Heading>
              </HStack>
              <Badge
                colorScheme={article.length === 'short' ? 'blue' : 'purple'}
                fontSize="xs"
                px={2}
                py={1}
                borderRadius="full"
              >
                {article.length === 'short' ? 'Short' : 'Long'}
              </Badge>
            </Flex>

            <Text
              color={secondaryTextColor}
              fontSize="sm"
              noOfLines={3}
              lineHeight="tall"
            >
              {previewContent}
            </Text>

            <HStack spacing={4} justify="space-between" mt="auto" pt={2}>
              <HStack spacing={1}>
                <Icon as={FiGlobe} fontSize="sm" color={secondaryTextColor} />
                <Text fontSize="xs" color={secondaryTextColor} fontWeight="medium">
                  {getLanguageDisplayName(article.language)}
                </Text>
              </HStack>

              <HStack spacing={1}>
                <Icon as={FiCalendar} fontSize="sm" color={secondaryTextColor} />
                <Text fontSize="xs" color={secondaryTextColor}>
                  {formattedDate}
                </Text>
              </HStack>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </Link>
  );
}